import React, { memo } from 'react'
import { useSelector } from 'react-redux'
import styled from 'styled-components'
import { useMediaPredicate } from 'react-media-hook'
import { useNavigate, useParams } from 'react-router-dom'

import { RootState } from '../../../partner/redux/store'
import {
    useLoadBalancerGroupGetByIdQuery,
    useLoadBalancerGroupGetChargePointsForGroupQuery,
    useLoadBalancerGroupDeleteMutation,
    useMeasurementDevicesGetByIdQuery,
} from '../../redux/api/web-api'
import { LoadbalancerCurrentLimitWidget } from '../../features/loadbalancer-group/loadbalancer-current-limit-widget'
import { LoadbalancerDetailsWidget } from '../../features/loadbalancer-group/loadbalancer-details-widget'
import { LBChargePointList } from '../../../support/components/lb-charge-point-list'
import { PageLayoutContainer } from '../../../lib/page-layout-container'
import { notifyError, notifySuccess } from '../../features/notificationsManager/Notify'
import { isAdminDomain } from '../../../lib/currentDomain'
import LoadBalancerTypeLabel from '../LoadbalancerTypeLabel'
import { SPageControlsWrapper } from '../../features/charge-point-page-controls'
import { LoadBalancerDetailsInputs } from '../../features/loadbalancer-group/loadbalancer-details-inputs'
import { LoadBalancerElectricalInputs } from '../../features/loadbalancer-group/loadbalancer-electrical-inputs'
import InlineAlert, { AlertType } from '../InlineAlert'
import { useCompanyQuery } from '../../hooks/useFetchEndpoints'
import useText from '../../hooks/useText'
import { confirm } from '../../uikit/ConfirmDialog'

export const SLBContainer = styled.div`
    min-height: 250px;
    margin-top: 15px;
    &::-webkit-scrollbar {
        display: none;
    }
    @media screen and (max-width: 900px) {
        width: 100%;
    }
`

export const LoadBalancerDetailsWidgetPage = memo(function LoadBalancerDetailsWidgetPage(): React.ReactElement {
    const text = useText()
    const navigate = useNavigate()
    const isMobileScreen = useMediaPredicate('(max-width: 900px)')
    const { id: lbGroupId } = useParams()
    const partnerId = useSelector((state: RootState) => state.user.partnerId)

    const {
        data: loadBalancerGroup,
        refetch: refetchGroup,
        isLoading: loadingGroup,
    } = useLoadBalancerGroupGetByIdQuery(
        { id: lbGroupId && lbGroupId },
        { skip: !lbGroupId, refetchOnMountOrArgChange: true }
    )

    const {
        data: chargePoints,
        isFetching: ChargePointFetching,
        isLoading: chargePointsdataLoading,
        refetch: refreshChargePoints,
    } = useLoadBalancerGroupGetChargePointsForGroupQuery(
        {
            id: lbGroupId,
            expand: 'additionalInfo,ocppConfig',
        },
        { skip: !loadBalancerGroup }
    )

    const { data: companies } = useCompanyQuery(partnerId)
    const { currentData: measurementDevice } = useMeasurementDevicesGetByIdQuery(
        { idOrSerialNumber: loadBalancerGroup?.measurementDeviceId?.toString() ?? '' },
        { skip: !loadBalancerGroup?.measurementDeviceId }
    )

    const [removeLoadBalancerGroup] = useLoadBalancerGroupDeleteMutation()

    const handleRemoveLBG = async (groupId: number) => {
        if (!groupId) return

        const confirmed = await confirm({
            header: text('lblProceedConfirmation'),
            message: text('lblDeleteLoadBalancerWithId', { lbgId: groupId }),
            variant: 'danger',
        })
        if (!confirmed) return

        try {
            await removeLoadBalancerGroup({ id: groupId }).unwrap()
            notifySuccess(text('lblLoadBalancerDeleteSuccess'))
            navigate('/loadbalancer')
        } catch (error) {
            notifyError(text('lblLoadBalancerDeleteError'))
        }
    }

    return (
        <PageLayoutContainer>
            <SPageControlsWrapper>
                {loadBalancerGroup && (
                    <LoadBalancerTypeLabel
                        data-testid="LoadBalancer-type-label"
                        loadBalancerGroup={loadBalancerGroup}
                        editable={!isAdminDomain}
                        hasChargers={chargePoints?.length ? true : false}
                        removeLBGroup={handleRemoveLBG}
                    />
                )}
            </SPageControlsWrapper>

            {isAdminDomain && loadBalancerGroup && (
                <>
                    {loadBalancerGroup?.active && (
                        <InlineAlert text={text('msgLbActive')} alertType={AlertType.Info} data-testid="edit-msg" />
                    )}

                    <LoadbalancerDetailsWidget
                        loading={loadingGroup}
                        measurementDevice={measurementDevice}
                        group={loadBalancerGroup}
                    ></LoadbalancerDetailsWidget>

                    <div className="grid">
                        <div className={isMobileScreen ? 'col-12' : 'col-6'}>
                            <LoadbalancerCurrentLimitWidget
                                group={loadBalancerGroup}
                                refetchGroup={refetchGroup}
                            ></LoadbalancerCurrentLimitWidget>
                        </div>
                        <div className={isMobileScreen ? 'col-12' : 'col-6'}>
                            <LBChargePointList
                                chargePoints={chargePoints ?? []}
                                isGroupActive={loadBalancerGroup?.active ?? false}
                                group={loadBalancerGroup}
                                isFetching={ChargePointFetching}
                                isLoading={chargePointsdataLoading}
                                updateChargers={refreshChargePoints}
                            ></LBChargePointList>
                        </div>
                    </div>
                </>
            )}

            {!isAdminDomain && loadBalancerGroup && (
                <div className="grid">
                    <div className={isMobileScreen ? 'col-12' : 'col-6'}>
                        <LoadBalancerDetailsInputs
                            loadBalancerGroup={loadBalancerGroup}
                            companies={companies ?? []}
                            hasChargers={chargePoints?.length ? true : false}
                        />
                    </div>

                    <div className={isMobileScreen ? 'col-12' : 'col-6'}>
                        {loadBalancerGroup && (
                            <LoadbalancerCurrentLimitWidget
                                group={loadBalancerGroup}
                                refetchGroup={refetchGroup}
                            ></LoadbalancerCurrentLimitWidget>
                        )}
                    </div>
                </div>
            )}
            {loadBalancerGroup && !isAdminDomain && (
                <div className="grid">
                    <div className={isMobileScreen ? 'col-12' : 'col-6'}>
                        {loadBalancerGroup && !isAdminDomain && (
                            <LoadBalancerElectricalInputs
                                loadBalancerGroup={loadBalancerGroup}
                                hasChargers={chargePoints?.length ? true : false}
                                getMeasurementDevice={measurementDevice ?? undefined}
                            />
                        )}
                    </div>
                    <div className={isMobileScreen ? 'col-12' : 'col-6'}>
                        <LBChargePointList
                            chargePoints={chargePoints ?? []}
                            isGroupActive={loadBalancerGroup?.active ?? false}
                            group={loadBalancerGroup}
                            isFetching={ChargePointFetching}
                            isLoading={chargePointsdataLoading}
                            updateChargers={refreshChargePoints}
                        ></LBChargePointList>
                    </div>
                </div>
            )}
        </PageLayoutContainer>
    )
})
